import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { MAGAZINE_ARTICLES } from '../../data/magazine';
import {
  Plus, Trash2, Eye, EyeOff, Save, Calendar, Image as ImageIcon,
  Sparkles, AlertCircle, Loader2, Newspaper, ArrowUpRight, Star,
} from 'lucide-react';

/**
 * Admin · Hero Editions — gestion des éditions hebdomadaires du hero d'accueil.
 *
 * Concept "magazine hebdomadaire" : chaque édition est associée à une
 * (année, semaine ISO). On peut publier/dépublier, planifier, modifier
 * image / titre / sous-titre. L'édition de la semaine courante est
 * affichée prioritairement en page d'accueil ; à défaut, la dernière
 * publiée. Si aucune édition publiée → fallback statique côté front.
 *
 * Format de référence (Volume — Mois Année) :
 *   "Volume 19 — Mai 2026"
 */
export default function AdminHeroEditions() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = pas d'édition ouverte
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await api.adminListHeroEditions();
      setItems(data.items || []);
      setMeta({
        current_year: data.current_year,
        current_week: data.current_week,
        current_volume_label: data.current_volume_label,
      });
    } catch (e) {
      toast.error("Impossible de charger les éditions : " + e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const startCreate = () => {
    setCreating(true);
    setEditing({
      year: meta?.current_year || new Date().getFullYear(),
      week: meta?.current_week || 1,
      title: '',
      subtitle: '',
      kicker: 'PRÉPARATION ETM 2026',
      image_url: '',
      link_url: '',
      link_label: '',
      theme_focus: '',
      status: 'draft',
    });
  };

  const startEdit = (item) => {
    setCreating(false);
    setEditing({ ...item });
  };

  const cancelEdit = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      if (creating) {
        await api.adminCreateHeroEdition(editing);
        toast.success(`Édition créée pour la semaine ${editing.week}`);
      } else {
        await api.adminUpdateHeroEdition(editing.id, editing);
        toast.success("Édition mise à jour");
      }
      cancelEdit();
      await reload();
    } catch (e) {
      toast.error(e.message || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (item) => {
    try {
      if (item.status === 'published') {
        await api.adminUpdateHeroEdition(item.id, { status: 'draft' });
        toast.success(`"${item.title || item.volume_label}" dépubliée`);
      } else {
        await api.adminPublishHeroEdition(item.id);
        toast.success(`"${item.title || item.volume_label}" publiée`);
      }
      await reload();
    } catch (e) {
      toast.error(e.message || "Erreur lors du changement de statut");
    }
  };

  const remove = async (item) => {
    const label = item.title || item.volume_label || `semaine ${item.week}`;
    if (!window.confirm(`Supprimer définitivement l'édition "${label}" ?`)) return;
    try {
      await api.adminDeleteHeroEdition(item.id);
      toast.success("Édition supprimée");
      await reload();
    } catch (e) {
      toast.error(e.message);
    }
  };

  // ---- Promotion rapide d'un article magazine ----
  const [promoteSlug, setPromoteSlug] = useState('');
  const [promoting, setPromoting] = useState(false);

  const articleOptions = useMemo(
    () =>
      MAGAZINE_ARTICLES.map((a) => ({
        slug: a.slug,
        title: a.title || a.shortTitle,
        kicker: a.kicker,
        subtitle: a.subtitle,
        image_url: a.heroImage,
      })),
    []
  );

  const promoteArticle = async () => {
    if (!promoteSlug) {
      toast.error('Sélectionne un article à promouvoir');
      return;
    }
    const article = MAGAZINE_ARTICLES.find((a) => a.slug === promoteSlug);
    if (!article) {
      toast.error('Article introuvable');
      return;
    }
    setPromoting(true);
    try {
      const res = await api.adminPromoteFromMagazine({
        slug: article.slug,
        title: article.title || article.shortTitle,
        subtitle: article.subtitle,
        kicker: article.kicker
          ? article.kicker.toUpperCase()
          : 'GUIDE OFFICIEL · SÉCURITÉ ROUTIÈRE 2026',
        image_url: article.heroImage,
        link_label: 'Lire le guide officiel',
        move_existing: true,
      });
      toast.success(
        `"${article.title}" est maintenant à la une (Volume ${res.week} — ${meta?.current_volume_label?.split('— ')[1] || ''})`
      );
      setPromoteSlug('');
      await reload();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la promotion');
    } finally {
      setPromoting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-hero-editions-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-black tracking-tight flex items-center gap-2">
            <Newspaper className="w-6 h-6 text-[hsl(var(--primary))]" />
            Éditions Hebdomadaires
          </h1>
          <p className="text-sm text-foreground/70 mt-1">
            Gestion du hero magazine de la page d'accueil.
            {meta && (
              <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary-dark))] text-xs font-bold">
                <Calendar className="w-3 h-3" />
                Semaine actuelle : {meta.current_volume_label}
              </span>
            )}
          </p>
        </div>
        <Button onClick={startCreate} className="mc-btn-primary gap-1.5" data-testid="hero-create-btn">
          <Plus className="w-4 h-4" /> Nouvelle édition
        </Button>
      </div>

      {/* ===== Promotion rapide d'un article magazine en une ===== */}
      <div
        className="mc-card p-4 sm:p-5 border-l-4 border-amber-500/60 bg-amber-500/[0.04]"
        data-testid="promote-magazine-card"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="w-9 h-9 rounded-md bg-amber-500/15 text-amber-500 flex items-center justify-center flex-shrink-0">
            <Star className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-black text-base text-foreground">
              Promouvoir un article en une
            </h3>
            <p className="text-xs text-foreground/70 mt-0.5">
              Sélectionne un article du magazine pour le mettre instantanément à
              la une de l'accueil pour la semaine ISO en cours
              {meta?.current_volume_label ? ` (${meta.current_volume_label})` : ''}.
              Si une autre édition occupe déjà ce slot, elle sera décalée
              automatiquement vers la semaine libre la plus proche.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_auto] gap-2 sm:gap-3 items-end">
          <div>
            <Label htmlFor="promote-slug" className="text-xs">
              Article magazine
            </Label>
            <Select value={promoteSlug} onValueChange={setPromoteSlug}>
              <SelectTrigger
                id="promote-slug"
                data-testid="promote-magazine-select"
              >
                <SelectValue placeholder="Choisir un article…" />
              </SelectTrigger>
              <SelectContent className="max-h-[320px]">
                {articleOptions.map((opt) => (
                  <SelectItem key={opt.slug} value={opt.slug}>
                    {opt.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={promoteArticle}
            disabled={!promoteSlug || promoting}
            className="mc-btn-primary gap-1.5"
            data-testid="promote-magazine-btn"
          >
            {promoting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Star className="w-4 h-4" />
            )}
            Mettre à la une
          </Button>
        </div>
      </div>

      {/* Editor (création ou édition) */}
      {editing && (
        <div className="mc-card p-5 sm:p-6 border-l-4 border-[hsl(var(--primary))]" data-testid="hero-edition-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-black tracking-tight">
              {creating ? "Nouvelle édition" : "Modifier l'édition"}
            </h2>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={cancelEdit} disabled={saving} data-testid="hero-cancel-btn">
                Annuler
              </Button>
              <Button onClick={save} disabled={saving} className="mc-btn-primary gap-1.5" data-testid="hero-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Enregistrer
              </Button>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="year">Année</Label>
              <Input
                id="year"
                type="number"
                value={editing.year}
                onChange={(e) => setEditing({ ...editing, year: parseInt(e.target.value, 10) || 2026 })}
                min={2020}
                max={2099}
                data-testid="hero-input-year"
              />
            </div>
            <div>
              <Label htmlFor="week">Semaine ISO (1-53)</Label>
              <Input
                id="week"
                type="number"
                value={editing.week}
                onChange={(e) => setEditing({ ...editing, week: parseInt(e.target.value, 10) || 1 })}
                min={1}
                max={53}
                data-testid="hero-input-week"
              />
              <p className="text-xs text-foreground/60 mt-1">
                Ex : semaine 19 = 4-10 mai 2026 →{' '}
                <span className="font-bold">Volume {editing.week} — automatique</span>
              </p>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="kicker">Kicker (petite étiquette rouge en haut)</Label>
              <Input
                id="kicker"
                value={editing.kicker || ''}
                onChange={(e) => setEditing({ ...editing, kicker: e.target.value })}
                placeholder="ÉDITION SPÉCIALE SÉCURITÉ"
                data-testid="hero-input-kicker"
              />
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="title">Titre éditorial</Label>
              <Input
                id="title"
                value={editing.title || ''}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="Le casque, ton meilleur allié"
                data-testid="hero-input-title"
              />
              <p className="text-xs text-foreground/60 mt-1">
                Si laissé vide, le hero affichera le slogan statique "La route se partage…"
              </p>
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="subtitle">Sous-titre / accroche</Label>
              <Textarea
                id="subtitle"
                value={editing.subtitle || ''}
                onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })}
                placeholder="Cette semaine, focus sur le port du casque homologué et son rôle vital sur deux roues."
                rows={3}
                data-testid="hero-input-subtitle"
              />
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="image_url" className="flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> URL de l'image de fond
              </Label>
              <Input
                id="image_url"
                value={editing.image_url || ''}
                onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                placeholder="https://images.unsplash.com/photo-..."
                data-testid="hero-input-image"
              />
              <p className="text-xs text-foreground/60 mt-1">
                Format recommandé : paysage 1600×900 px minimum. Si vide → image par défaut moto crépuscule.
              </p>
              {editing.image_url && (
                <div className="mt-3 rounded-md overflow-hidden border border-border max-h-48">
                  <img
                    src={editing.image_url}
                    alt="Aperçu"
                    className="w-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="theme_focus">Thème mis en avant (optionnel)</Label>
              <Select
                value={editing.theme_focus || 'none'}
                onValueChange={(v) => setEditing({ ...editing, theme_focus: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="hero-input-theme">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  <SelectItem value="C">C — Circulation</SelectItem>
                  <SelectItem value="L">L — Conducteur</SelectItem>
                  <SelectItem value="R">R — Route</SelectItem>
                  <SelectItem value="U">U — Autres usagers</SelectItem>
                  <SelectItem value="D">D — Notions diverses</SelectItem>
                  <SelectItem value="S">S — Premiers secours</SelectItem>
                  <SelectItem value="M">M — Mécanique</SelectItem>
                  <SelectItem value="P">P — Équipement</SelectItem>
                  <SelectItem value="E">E — Environnement</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="link_url">Lien article (URL relative ou absolue)</Label>
                <Input
                  id="link_url"
                  placeholder="/magazine/bien-choisir-casque-moto"
                  value={editing.link_url || ''}
                  onChange={(e) => setEditing({ ...editing, link_url: e.target.value })}
                  data-testid="hero-input-link-url"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Si renseigné, un bouton CTA "Lire l'article" s'affichera sur le hero.
                </p>
              </div>
              <div>
                <Label htmlFor="link_label">Libellé du bouton CTA</Label>
                <Input
                  id="link_label"
                  placeholder="Lire l'article"
                  value={editing.link_label || ''}
                  onChange={(e) => setEditing({ ...editing, link_label: e.target.value })}
                  data-testid="hero-input-link-label"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Par défaut : "Lire l'article". Personnalisable (ex : "Découvrir le guide").
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="status">Statut</Label>
              <Select
                value={editing.status}
                onValueChange={(v) => setEditing({ ...editing, status: v })}
              >
                <SelectTrigger data-testid="hero-input-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">📝 Brouillon</SelectItem>
                  <SelectItem value="published">✅ Publié</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-foreground/60">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement des éditions…
        </div>
      ) : items.length === 0 ? (
        <div className="mc-card p-10 text-center" data-testid="hero-empty-state">
          <Sparkles className="w-12 h-12 mx-auto text-[hsl(var(--primary))]/40 mb-3" />
          <h3 className="text-lg font-black mb-2">Aucune édition pour l'instant</h3>
          <p className="text-sm text-foreground/70 mb-4 max-w-md mx-auto">
            Crée ta première édition hebdomadaire. Le hero d'accueil affichera
            automatiquement l'édition de la semaine courante (ou la dernière publiée).
          </p>
          <Button onClick={startCreate} className="mc-btn-primary gap-1.5">
            <Plus className="w-4 h-4" /> Créer la première édition
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((it) => {
            const isPublished = it.status === 'published';
            const isCurrent = it.is_current_week;
            return (
              <div
                key={it.id}
                className={`mc-card p-4 sm:p-5 flex flex-col sm:flex-row gap-4 ${
                  isCurrent ? 'border-l-4 border-[hsl(var(--primary))]' : ''
                }`}
                data-testid={`hero-edition-item-${it.id}`}
              >
                {/* Cover thumbnail */}
                <div className="w-full sm:w-32 h-32 sm:h-24 rounded-md overflow-hidden bg-muted flex-shrink-0">
                  {it.image_url ? (
                    <img src={it.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground/30">
                      <ImageIcon className="w-8 h-8" />
                    </div>
                  )}
                </div>

                {/* Infos */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <span className="text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))]">
                      {it.volume_label}
                    </span>
                    {isCurrent && (
                      <Badge variant="outline" className="bg-[hsl(var(--primary))] text-white border-transparent text-[10px] gap-1">
                        <Calendar className="w-3 h-3" /> Cette semaine
                      </Badge>
                    )}
                    {isPublished ? (
                      <Badge variant="outline" className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] gap-1">
                        <Eye className="w-3 h-3" /> Publiée
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px] gap-1">
                        <EyeOff className="w-3 h-3" /> Brouillon
                      </Badge>
                    )}
                    {it.theme_focus && (
                      <Badge variant="outline" className="text-[10px]">Thème {it.theme_focus}</Badge>
                    )}
                  </div>
                  <h3 className="font-black text-base text-foreground truncate">
                    {it.title || <span className="italic text-foreground/50">Pas de titre — fallback statique</span>}
                  </h3>
                  {it.subtitle && (
                    <p className="text-sm text-foreground/70 mt-1 line-clamp-2">{it.subtitle}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap sm:flex-col sm:gap-1.5 sm:items-end">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => togglePublish(it)}
                    className="gap-1.5"
                    data-testid={`hero-toggle-${it.id}`}
                  >
                    {isPublished ? <><EyeOff className="w-3.5 h-3.5" /> Dépublier</> : <><Eye className="w-3.5 h-3.5" /> Publier</>}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => startEdit(it)} className="gap-1.5" data-testid={`hero-edit-${it.id}`}>
                    <ArrowUpRight className="w-3.5 h-3.5" /> Modifier
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it)} className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`hero-delete-${it.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Supprimer
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Aide */}
      <div className="mc-card mc-card-accent-official p-4 text-sm text-foreground/75">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 mt-0.5 text-[hsl(var(--primary))] flex-shrink-0" />
          <div>
            <p className="font-bold text-foreground mb-1">Logique d'affichage du hero</p>
            <ol className="list-decimal pl-4 space-y-0.5">
              <li>Si une édition <strong>publiée</strong> existe pour la semaine ISO en cours → elle s'affiche prioritairement.</li>
              <li>Sinon, la dernière édition publiée (la plus récente) est affichée.</li>
              <li>Si aucune édition publiée → fallback dynamique avec uniquement le n° de volume calculé automatiquement.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
